/**
 * Astrologer Admin - KYC Approvals
 * Queue for astrologer KYC verification and approval
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  Avatar,
  Tabs,
  Tab,
  Divider,
  ImageList,
  ImageListItem,
} from '@mui/material';
import {
  Search,
  Visibility,
  CheckCircle,
  Cancel,
  Refresh,
  Star,
  VerifiedUser,
  PendingActions,
  Download,
  Person,
  Badge,
  Phone,
  Email,
} from '@mui/icons-material';
import axios from 'axios';
import { api_urls } from '../../../utils/api-urls';
import { toast } from 'react-toastify';

const AstrologerKYC = () => {
  const [astrologers, setAstrologers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [expertiseFilter, setExpertiseFilter] = useState('all');
  const [selectedAstrologer, setSelectedAstrologer] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [tabValue, setTabValue] = useState(0);

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });

  const expertiseOptions = [
    'Vedic Astrology', 'Tarot Reading', 'Numerology', 'Palmistry', 'Vastu',
    'Face Reading', 'Prashna Kundli', 'KP Astrology', 'Lal Kitab', 'Horary'
  ];

  const languageOptions = [
    'Hindi', 'English', 'Tamil', 'Telugu', 'Bengali', 'Marathi', 'Gujarati', 'Kannada'
  ];

  useEffect(() => {
    fetchAstrologers();
    fetchStats();
  }, [statusFilter, expertiseFilter]);

  const fetchAstrologers = async () => {
    setLoading(true);
    try {
      const params = { kycStatus: statusFilter };
      if (expertiseFilter !== 'all') params.expertise = expertiseFilter;

      const response = await axios.get(`${api_urls}api/astrologer/admin/list`, { params });
      setAstrologers(response.data?.astrologers || []);
    } catch (error) {
      console.error('Error fetching astrologers:', error);
      // Demo data for development
      setAstrologers([
        {
          _id: '1',
          astrologerCode: 'AST001',
          astrologerName: 'Pandit Ramesh Sharma',
          phoneNumber: '9876543210',
          email: 'ramesh.astro@gmail.com',
          expertise: ['Vedic Astrology', 'Tarot Reading'],
          languages: ['Hindi', 'English'],
          experience: '15 years',
          aadharNumber: '****5678',
          kycStatus: 'PENDING',
          isVerified: false,
          kycDocuments: {
            aadharFront: 'https://via.placeholder.com/400x250?text=Aadhar+Front',
            aadharBack: 'https://via.placeholder.com/400x250?text=Aadhar+Back',
            panCard: 'https://via.placeholder.com/400x250?text=PAN+Card',
            photo: 'https://via.placeholder.com/300x300?text=Photo',
            certificate: 'https://via.placeholder.com/400x250?text=Certificate'
          },
          chat_price: 15,
          call_price: 20,
          video_call_price: 25,
          rating: 0,
          totalConsultations: 0,
          wallet_balance: 0,
          bankDetails: {
            accountName: 'Ramesh Sharma',
            accountNumber: '****1234',
            ifscCode: 'SBIN0001234',
            bankName: 'State Bank of India'
          },
          address: {
            city: 'Varanasi',
            state: 'Uttar Pradesh',
            pincode: '221001'
          },
          createdAt: new Date().toISOString(),
        },
        {
          _id: '2',
          astrologerCode: 'AST002',
          astrologerName: 'Guru Lakshmi Devi',
          phoneNumber: '9876543211',
          email: 'lakshmi.astro@gmail.com',
          expertise: ['Numerology', 'Palmistry'],
          languages: ['Hindi', 'Tamil'],
          experience: '10 years',
          aadharNumber: '****9012',
          kycStatus: 'PENDING',
          isVerified: false,
          kycDocuments: {
            aadharFront: 'https://via.placeholder.com/400x250?text=Aadhar+Front',
            aadharBack: 'https://via.placeholder.com/400x250?text=Aadhar+Back',
            panCard: 'https://via.placeholder.com/400x250?text=PAN+Card',
            photo: 'https://via.placeholder.com/300x300?text=Photo'
          },
          chat_price: 12,
          call_price: 18,
          video_call_price: 22,
          rating: 0,
          totalConsultations: 0,
          wallet_balance: 0,
          bankDetails: {
            accountName: 'Lakshmi Devi',
            accountNumber: '****5678',
            ifscCode: 'ICIC0001234',
            bankName: 'ICICI Bank'
          },
          address: {
            city: 'Chennai',
            state: 'Tamil Nadu',
            pincode: '600001'
          },
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${api_urls}api/astrologer/admin/stats`);
      setStats(response.data || { total: 250, pending: 18, approved: 225, rejected: 7 });
    } catch (error) {
      setStats({ total: 250, pending: 18, approved: 225, rejected: 7 });
    }
  };

  const handleApproveKyc = async (astrologerId) => {
    try {
      await axios.put(`${api_urls}api/astrologer/admin/${astrologerId}/kyc/approve`);
      toast.success('Astrologer KYC approved successfully');
      fetchAstrologers();
      fetchStats();
      setDetailDialogOpen(false);
    } catch (error) {
      toast.error('Failed to approve KYC');
    }
  };

  const handleRejectKyc = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide rejection reason');
      return;
    }
    try {
      await axios.put(`${api_urls}api/astrologer/admin/${selectedAstrologer._id}/kyc/reject`, {
        reason: rejectReason
      });
      toast.success('Astrologer KYC rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
      fetchAstrologers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to reject KYC');
    }
  };

  const getKycStatusChip = (status) => {
    const config = {
      PENDING: { color: 'warning', label: 'Pending', icon: <PendingActions /> },
      APPROVED: { color: 'success', label: 'Approved', icon: <VerifiedUser /> },
      REJECTED: { color: 'error', label: 'Rejected', icon: <Cancel /> },
    };
    const cfg = config[status] || config.PENDING;
    return <Chip size="small" color={cfg.color} label={cfg.label} icon={cfg.icon} />;
  };

  const filteredAstrologers = astrologers.filter(astro =>
    astro.astrologerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    astro.phoneNumber?.includes(searchTerm) ||
    astro.astrologerCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    astro.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#9C27B0' }}>
        <Star sx={{ mr: 1, verticalAlign: 'middle' }} />
        Astrologer KYC Approvals
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#f3e5f5' }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#9C27B0' }}>
                {stats.total}
              </Typography>
              <Typography color="textSecondary">Total Astrologers</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#f57c00' }}>
                {stats.pending}
              </Typography>
              <Typography color="textSecondary">Pending KYC</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#388e3c' }}>
                {stats.approved}
              </Typography>
              <Typography color="textSecondary">Approved</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#ffebee' }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                {stats.rejected}
              </Typography>
              <Typography color="textSecondary">Rejected</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search astrologers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>KYC Status</InputLabel>
              <Select
                value={statusFilter}
                label="KYC Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Expertise</InputLabel>
              <Select
                value={expertiseFilter}
                label="Expertise"
                onChange={(e) => setExpertiseFilter(e.target.value)}
              >
                <MenuItem value="all">All Expertise</MenuItem>
                {expertiseOptions.map(exp => (
                  <MenuItem key={exp} value={exp}>{exp}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => { fetchAstrologers(); fetchStats(); }}
              fullWidth
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Astrologers Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>S.No</TableCell>
              <TableCell>Astrologer</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Expertise</TableCell>
              <TableCell>Experience</TableCell>
              <TableCell>Prices (₹/min)</TableCell>
              <TableCell>KYC Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredAstrologers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  No astrologers found
                </TableCell>
              </TableRow>
            ) : (
              filteredAstrologers.map((astro, index) => (
                <TableRow key={astro._id} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar src={astro.kycDocuments?.photo} />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {astro.astrologerName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {astro.astrologerCode}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{astro.phoneNumber}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {astro.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(astro.expertise || []).slice(0, 2).map((exp, i) => (
                        <Chip key={i} label={exp} size="small" sx={{ bgcolor: '#f3e5f5', color: '#9C27B0' }} />
                      ))}
                      {(astro.expertise || []).length > 2 && (
                        <Chip label={`+${astro.expertise.length - 2}`} size="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{astro.experience}</TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">Chat: ₹{astro.chat_price}</Typography>
                    <Typography variant="caption" display="block">Call: ₹{astro.call_price}</Typography>
                    <Typography variant="caption" display="block">Video: ₹{astro.video_call_price}</Typography>
                  </TableCell>
                  <TableCell>{getKycStatusChip(astro.kycStatus)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedAstrologer(astro);
                            setDetailDialogOpen(true);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {astro.kycStatus === 'PENDING' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApproveKyc(astro._id)}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedAstrologer(astro);
                                setRejectDialogOpen(true);
                              }}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#9C27B0', color: 'white' }}>
          <Star sx={{ mr: 1, verticalAlign: 'middle' }} />
          Astrologer Details - {selectedAstrologer?.astrologerName}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedAstrologer && (
            <Box>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                <Tab label="Basic Info" />
                <Tab label="Documents" />
                <Tab label="Bank Details" />
              </Tabs>
              
              {tabValue === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Avatar
                      src={selectedAstrologer.kycDocuments?.photo}
                      sx={{ width: 150, height: 150, mx: 'auto', mb: 2 }}
                    />
                    <Typography align="center" variant="h6">
                      {selectedAstrologer.astrologerName}
                    </Typography>
                    <Typography align="center" color="textSecondary">
                      {selectedAstrologer.astrologerCode}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">Phone</Typography>
                        <Typography>{selectedAstrologer.phoneNumber}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">Email</Typography>
                        <Typography>{selectedAstrologer.email}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">Experience</Typography>
                        <Typography>{selectedAstrologer.experience}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">Aadhar</Typography>
                        <Typography>{selectedAstrologer.aadharNumber}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">Expertise</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {(selectedAstrologer.expertise || []).map((exp, i) => (
                            <Chip key={i} label={exp} size="small" color="primary" />
                          ))}
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">Languages</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {(selectedAstrologer.languages || []).map((lang, i) => (
                            <Chip key={i} label={lang} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Pricing (₹/min)</Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Typography variant="caption">Chat Price</Typography>
                            <Typography fontWeight={600}>₹{selectedAstrologer.chat_price}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption">Call Price</Typography>
                            <Typography fontWeight={600}>₹{selectedAstrologer.call_price}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption">Video Price</Typography>
                            <Typography fontWeight={600}>₹{selectedAstrologer.video_call_price}</Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">Address</Typography>
                        <Typography>
                          {selectedAstrologer.address?.city}, {selectedAstrologer.address?.state} - {selectedAstrologer.address?.pincode}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              
              {tabValue === 1 && (
                <Grid container spacing={2}>
                  {selectedAstrologer.kycDocuments?.aadharFront && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Aadhar Front</Typography>
                      <img
                        src={selectedAstrologer.kycDocuments.aadharFront}
                        alt="Aadhar Front"
                        style={{ width: '100%', borderRadius: 8, border: '1px solid #ddd' }}
                      />
                    </Grid>
                  )}
                  {selectedAstrologer.kycDocuments?.aadharBack && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Aadhar Back</Typography>
                      <img
                        src={selectedAstrologer.kycDocuments.aadharBack}
                        alt="Aadhar Back"
                        style={{ width: '100%', borderRadius: 8, border: '1px solid #ddd' }}
                      />
                    </Grid>
                  )}
                  {selectedAstrologer.kycDocuments?.panCard && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>PAN Card</Typography>
                      <img
                        src={selectedAstrologer.kycDocuments.panCard}
                        alt="PAN Card"
                        style={{ width: '100%', borderRadius: 8, border: '1px solid #ddd' }}
                      />
                    </Grid>
                  )}
                  {selectedAstrologer.kycDocuments?.certificate && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Certificate</Typography>
                      <img
                        src={selectedAstrologer.kycDocuments.certificate}
                        alt="Certificate"
                        style={{ width: '100%', borderRadius: 8, border: '1px solid #ddd' }}
                      />
                    </Grid>
                  )}
                </Grid>
              )}
              
              {tabValue === 2 && selectedAstrologer.bankDetails && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Account Holder Name</Typography>
                    <Typography>{selectedAstrologer.bankDetails.accountName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Account Number</Typography>
                    <Typography>{selectedAstrologer.bankDetails.accountNumber}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">IFSC Code</Typography>
                    <Typography>{selectedAstrologer.bankDetails.ifscCode}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Bank Name</Typography>
                    <Typography>{selectedAstrologer.bankDetails.bankName}</Typography>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          {selectedAstrologer?.kycStatus === 'PENDING' && (
            <>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  setDetailDialogOpen(false);
                  setRejectDialogOpen(true);
                }}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleApproveKyc(selectedAstrologer._id)}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#d32f2f' }}>
          <Cancel sx={{ mr: 1, verticalAlign: 'middle' }} />
          Reject KYC - {selectedAstrologer?.astrologerName}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            placeholder="Please provide a reason for rejection..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRejectKyc}>
            Confirm Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AstrologerKYC;
